<template>

  <div>
    <div class="lg:tw-border lg:tw-border-solid lg:tw-border-gray-200 tw-rounded-lg tw-bg-white">
      <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3">

        <onboarding-plan-column
          class="tw-mb-5 lg:tw-mb-0 tw-rounded-lg tw-border tw-border-solid tw-border-gray-200 lg:!tw-border-none"
          :plan-name="titles.on_demand.planName"
          :title="titles.on_demand.title"
          :included="titles.on_demand.included"
          :base-prices="onDemandPricingOptions"
          :has-border-right="true"
          :round-top-left="true"
        >
          <template #select>
            <button class="btn btn-block text-left"
                    @click="selectedPriceId = 'on_demand'"
                    :disabled="!!subscription"
                    :class="selectedPlanType === 'on_demand' ? 'btn-primary' : 'btn-outline'">
              {{onDemandButtonLabel}}
            </button>

          </template>
        </onboarding-plan-column>

        <onboarding-plan-column
          class="tw-mb-5 lg:tw-mb-0 tw-rounded-lg tw-border tw-border-solid tw-border-gray-200 lg:!tw-border-none"
          :plan-name="titles.digital.planName"
          :title="titles.digital.title"
          :included="titles.digital.included"
          :extras="titles.digital.extras"
          :discounted-prices="digitalPricingOptions"
          :has-border-right="true"
          :discounted-price-value="discountedPriceValue('digital')"
        >
          <template #select>
            <OnboardingPriceSelectDropdown :prices="titles.digital.prices"
                                           :title="currentSelectOptionTitle('digital')"
                                           :selected="selectedPlanType === 'digital'"
                                           v-model="selectedPriceId"/>
          </template>
        </onboarding-plan-column>

        <onboarding-plan-column
          class="tw-mb-5 lg:tw-mb-0 tw-rounded-lg tw-border tw-border-solid tw-border-gray-200 lg:!tw-border-none"
          :plan-name="titles.all_access.planName"
          :title="titles.all_access.title"
          :included="titles.all_access.included"
          :extras="titles.all_access.extras"
          :discounted-prices="allAccessPricingOptions"
          :round-top-right="true"
          :discounted-price-value="discountedPriceValue('all_access')"
        >
          <template #select>
            <OnboardingPriceSelectDropdown :prices="titles.all_access.prices"
                                           :title="currentSelectOptionTitle('all_access')"
                                           :selected="selectedPlanType === 'all_access'"
                                           v-model="selectedPriceId"/>
          </template>
        </onboarding-plan-column>
      </div>
    </div>

    <slot name="bottom"
          :plans="plans"
          :commercialProducts="commercialProducts"
          :editorialProducts="editorialProducts"
          :terms="terms"
          :selectedPlanType="selectedPlanType"
          :selectedPriceId="selectedPriceId"
          :currentSelectOptionTitle="currentSelectOptionTitle"
          :planPrices="planPrices"
          :selectedSubscription="selectedSubscription"
          :updatedSubscriptionPriceId="updatedSubscriptionPriceId"
          :editingSubscription="editingSubscription"
    />

  </div>
</template>

<script>
import {PlansApi} from "../../Api/PlansApi.js";
import GeneralMixins from "../../../mixins/GeneralMixins.js";
import OnboardingPlanColumn from "./OnboardingPlanColumn.vue";
import OnboardingPriceSelectDropdown from "./OnboardingPriceSelectDropdown.vue";
import OnboardingPaymentModal from "./OnboardingPaymentModal.vue";
import {SubscriptionsApi} from "../../Api/SubscriptionsApi.js";

export default {
  components: {OnboardingPaymentModal, OnboardingPriceSelectDropdown, OnboardingPlanColumn},

  props: {
    value: {
      type: String,
      required: false,
      default: null
    },
    pricingPageMode: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  data() {
    return {
      showPaymentModal: false,
      plans: [],
      commercialProducts: [],
      editorialProducts: [],
      terms: "",
      loaded: false,
      subscription: null,
      approvedAssets: null
    }
  },


  mixins: [GeneralMixins],

  async mounted() {
    try {
      await this.loadData()
      if (this.signedIn) {
        try {
          const subscription = await SubscriptionsApi.getSubscription()
          this.subscription = subscription
          this.selectedPriceId = subscription.stripe_price_id
        } catch (e) {
          this.selectedPriceId = "on_demand"
        }
      }
    } catch (error) {
      this.showErrorToast("There was an error loading the plans. Please try again later.")
    }
  },

  methods: {

    async loadData() {
      const [plans, commercialProducts, editorialProducts, terms, approvedAssets] = await Promise.all([
        PlansApi.getPlans(),
        PlansApi.getProducts("commercial"),
        PlansApi.getProducts("editorial"),
        PlansApi.getTerms(),
        PlansApi.getApprovedAssets()
      ]);

      this.plans = plans;
      this.commercialProducts = commercialProducts;
      this.editorialProducts = editorialProducts;
      this.terms = terms;
      this.loaded = true;
      this.approvedAssets = approvedAssets
    },

    currentSelectOptionTitle(planType) {
      if (this.plans.length === 0 || this.selectedPriceId === null || planType === null || planType === "on_demand") {
        return "Select this plan"
      }

      if (planType === "digital") {
        if (this.currentSelectedDigitalPrice) {
          return `${this.currentSelectedDigitalPrice.name} / ${this.currentSelectedDigitalPrice.interval}`
        } else {
          return "Select this plan"
        }
      }

      if (planType === "all_access") {
        if (this.currentSelectedAllAccessPrice) {
          return `${this.currentSelectedAllAccessPrice.name} / ${this.currentSelectedAllAccessPrice.interval}`
        } else {
          return "Select this plan"
        }
      }
    },

    planPrices(planType) {
      if (this.plans.length === 0) {
        return []
      }

      return this.plans.filter(plan => plan.identifier === planType)
        .map(plan => plan.prices)
        .flat()
        .sort((a, b) => a.quantity - b.quantity)
    },

    findOnDemandPlan(versionSize, assetType) {
      return this.commercialProducts.find(plan => this.onDemandPlanHasAssetType(plan, versionSize, assetType))
    },

    onDemandPlanHasAssetType(plan, versionSize, assetType) {
      return plan.product_asset_types.find(p => p.version_size === versionSize && p.asset_type === assetType)
    },

    discountedPriceValue(planType) {
      if (this.plans.length === 0 || !planType) {
        return null
      }

      let price = null

      if (planType === "digital") {
        price = this.digitalPricingOptions[0].plan.amount / this.digitalPricingOptions[0].plan.quantity
      } else if (planType === "all_access") {
        price = this.allAccessPricingOptions[0].plan.amount / this.allAccessPricingOptions[0].plan.quantity
      }

      if (price) {
        return price / 100
      } else {
        return null
      }
    }
  },

  computed: {
    onDemandButtonLabel() {
      if (this.pricingPageMode) {
        if (this.subscription) {
          return "Select this Plan"
        } else {
          return "Current Plan"
        }
      } else {
        return "Select this Plan"
      }
    },
    editingSubscription() {
      return this.subscription !== null
    },

    updatedSubscriptionPriceId() {
      if (!this.subscription) {
        return false
      }

      return this.subscription.stripe_price_id !== this.selectedPriceId
    },

    signedIn() {
      return window.POC.uid
    },

    selectedPriceId: {
      get() {
        return this.value
      },
      set(selectedPriceId) {
        this.$emit('input', selectedPriceId)
      }
    },

    selectedSubscription() {
      if (this.selectedPriceId === null || this.selectedPriceId === "on_demand") {
        return null
      }

      const plan = this.plans.find(plan => plan.prices.find(price => price.id === this.selectedPriceId))
      const price = plan.prices.find(price => price.id === this.selectedPriceId)

      return {
        priceId: this.selectedPriceId,
        plan: plan,
        title: `${plan.product_name} / ${price.name} - $${price.amount / 100} / ${price.interval}`,
        priceName: price.name,
      }
    },

    titles() {
      return {
        on_demand: {
          planName: "A la carte plan",
          title: "Sign up for FREE account, and pay-as-you-go",
          included: [
            "Pay as you go",
            this.approvedAssets && `Access to ${this.approvedAssets.toLocaleString('en-US', {
              style: 'decimal',
              useGrouping: true,
              maximumFractionDigits: 0,
            })} assets`,
            "Projects & Collections",
            "Photo research services"
          ].filter(Boolean)
        },
        digital: {
          planName: "Small image plan",
          title: "Perfect resolution websites, blogs, social media, and mobile",
          included: [
            "Royalty-free, worldwide, unlimited usage",
            "72 DPI MD res photos",
            "Illustrated graphics (.jpg/.png)",
            "Standard HD footage (720 dpi)",
            "FREE Search Services",
            "Account Manager",
          ],
          // extras: [
          //   "Up to 5 users",
          //   "Unlimited lightboxes",
          // ],
          prices: this.planPrices("digital")
        },
        all_access: {
          planName: "Large image plan",
          title: "High resolution images for creative projects of any size",
          included: [
            "Royalty-free, worldwide, unlimited usage",
            "300 DPI LG res photos",
            "Vector illustrations (.eps/.svg)",
            "Full HD (1080 dpi) and 4k footage",
            "Unlimited Search Services",
            "Dedicated Account Manager",
          ],
          // extras: [
          //   "Unlimited users",
          //   "Unlimited lightboxes",
          //   "Unlimited search services"
          // ],
          prices: this.planPrices("all_access")
        }
      }
    },

    onDemandPricingOptions() {
      return [
        {
          id: 'sm',
          title: 'SM - 72 DPI photos & graphics',
          plan: this.findOnDemandPlan("small", "photo"),
        },
        {
          id: 'md',
          title: 'MD - 72 DPI photos, vectors, HD footage',
          plan: this.findOnDemandPlan("medium", "photo"),
        },
        {
          id: 'lg',
          title: 'LG - 300 DPI photos, 4k footage',
          plan: this.findOnDemandPlan("extra_large", "photo"),
        }
      ]
    },

    digitalPricingOptions() {
      return this.titles.digital.prices.map(price => {
        return {
          id: price.id,
          title: `${price.quantity} MD assets`,
          plan: price
        }
      })
    },

    allAccessPricingOptions() {
      return this.titles.all_access.prices.map(price => {
        return {
          id: price.id,
          title: `${price.quantity} LG assets`,
          plan: price
        }
      })
    },

    allPrices() {
      return this.plans.map(plan => plan.prices).flat()
    },

    selectedPlanType() {
      if (this.selectedPriceId === "on_demand") {
        return "on_demand";
      } else if (this.isDigitalPlanSelected) {
        return "digital";
      } else if (this.isAllAccessPlanSelected) {
        return "all_access";
      } else {
        return null;
      }
    },

    currentSelectedDigitalPrice() {
      return this.titles.digital.prices.find(price => price.id === this.selectedPriceId);
    },

    currentSelectedAllAccessPrice() {
      return this.titles.all_access.prices.find(price => price.id === this.selectedPriceId);
    },

    isDigitalPlanSelected() {
      return this.currentSelectedDigitalPrice !== undefined;
    },

    isAllAccessPlanSelected() {
      return this.currentSelectedAllAccessPrice !== undefined;
    },

    options() {
      return [
        {
          id: "customer",
          title: "Customer Account",
          description: "some description heresome description here some description here some description here some description here",
        },
        {
          id: "pending_contributor",
          title: "Contributor Account",
          description: "some description heresome description here some description here some description here some description here",
        }
      ]
    }
  }

}
</script>
